/**
 * iter 126.c — Custody swap requests.
 *
 * Compact inbox + "Request swap" dialog. Mounted on the Calendar
 * page (the natural home for custody UI). Reads/writes via:
 *   GET  /api/custody/swap-requests
 *   POST /api/custody/swap-requests
 *   POST /api/custody/swap-requests/{id}/respond
 *   POST /api/custody/swap-requests/{id}/cancel
 *
 * State machine at a glance: pending → approved | declined | cancelled.
 * Approvals materialise a custody_overrides row server-side; the
 * Calendar overlay picks it up on next refresh automatically.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowLeftRight, CheckCircle2, XCircle, Clock, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const STATUS_BADGE = {
  pending: { label: "Pending", cls: "bg-[#FBF0DC] border-[#E6CFA2] text-[#6F4E1E]" },
  approved: { label: "Approved", cls: "bg-[#D6F0EF] border-[#A7D8DA] text-[#0F5658]" },
  declined: { label: "Declined", cls: "bg-[#F3D9D6] border-[#E1B8B3] text-[#7A2E24]" },
  cancelled: { label: "Cancelled", cls: "bg-[#EEF3F4] border-[#D9E1E3] text-[#6F828A]" },
  expired: { label: "Expired", cls: "bg-[#EEF3F4] border-[#D9E1E3] text-[#6F828A]" },
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, {
      weekday: "short", year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

function nameFor(id, members, placeholder) {
  if (!id) return "—";
  const m = (members || []).find((x) => x.id === id);
  if (m) return m.name || m.email || "Parent";
  if (placeholder && placeholder.id === id) return `${placeholder.name || "Co-parent"} (placeholder)`;
  return "Parent";
}

export function CustodySwapRequestsCard({ members = [], placeholder = null, currentUserId, childList = [] }) {
  const [swaps, setSwaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/custody/swap-requests");
      setSwaps(r.data?.swap_requests || []);
    } catch {
      setSwaps([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const pending = useMemo(
    () => swaps.filter((s) => s.status === "pending"),
    [swaps],
  );
  const recent = useMemo(
    () => swaps.filter((s) => s.status !== "pending").slice(0, 5),
    [swaps],
  );

  const otherParent = useMemo(() => {
    const list = (members || []).filter((m) => m.id !== currentUserId);
    if (list.length) return list[0];
    if (placeholder) return { id: placeholder.id, name: placeholder.name, is_placeholder: true };
    return null;
  }, [members, placeholder, currentUserId]);

  const respond = async (swap, decision) => {
    try {
      await api.post(`/custody/swap-requests/${swap.id}/respond`, { decision });
      toast.success(decision === "approved" ? "Swap approved. Calendar override applied." : "Swap declined.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update swap request.");
    }
  };

  const cancel = async (swap) => {
    try {
      await api.post(`/custody/swap-requests/${swap.id}/cancel`, {});
      toast.success("Swap request cancelled.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel request.");
    }
  };

  if (loading && swaps.length === 0) return null;

  return (
    <div
      className="border border-[#D9E1E3] bg-white rounded-md p-4 sm:p-5 space-y-3"
      data-testid="custody-swap-requests-card"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">
            Custody swaps
          </div>
          <h2 className="font-heading text-base sm:text-lg font-medium text-[#0B2E32] mt-0.5 inline-flex items-center gap-2">
            <ArrowLeftRight className="w-4 h-4 text-[#116B6E]" strokeWidth={1.7} />
            Request a one-off custody swap
          </h2>
          <p className="text-xs text-[#6F828A] mt-1 max-w-prose">
            Mutual consent, court-ready audit trail. Approved swaps automatically override the schedule for that date.
          </p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          disabled={!otherParent}
          className="bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-sm h-9 px-3 flex-shrink-0"
          data-testid="cs-new-swap-btn"
        >
          + Request swap
        </Button>
      </div>

      {pending.length > 0 ? (
        <div className="space-y-2" data-testid="cs-pending-list">
          {pending.map((s) => (
            <SwapRow
              key={s.id}
              swap={s}
              members={members}
              placeholder={placeholder}
              currentUserId={currentUserId}
              childList={childList}
              onApprove={() => respond(s, "approved")}
              onDecline={() => respond(s, "declined")}
              onCancel={() => cancel(s)}
            />
          ))}
        </div>
      ) : (
        <div
          className="text-xs text-[#6F828A] border border-dashed border-[#D9E1E3] rounded-sm p-3"
          data-testid="cs-no-pending"
        >
          No pending swap requests. Tap “Request swap” to propose one for a specific date.
        </div>
      )}

      {recent.length > 0 && (
        <details className="text-xs text-[#6F828A]" data-testid="cs-history">
          <summary className="cursor-pointer select-none py-1">
            Recent decisions ({recent.length})
          </summary>
          <div className="space-y-1.5 mt-2">
            {recent.map((s) => (
              <SwapRow
                key={s.id}
                swap={s}
                members={members}
                placeholder={placeholder}
                currentUserId={currentUserId}
                childList={childList}
                compact
              />
            ))}
          </div>
        </details>
      )}

      <NewSwapDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        otherParent={otherParent}
        childList={childList}
        onCreated={async () => {
          setDialogOpen(false);
          await load();
        }}
      />
    </div>
  );
}


function SwapRow({ swap, members, placeholder, currentUserId, childList, onApprove, onDecline, onCancel, compact = false }) {
  const badge = STATUS_BADGE[swap.status] || STATUS_BADGE.pending;
  const isMine = swap.requested_by === currentUserId;
  const canApprove = swap.status === "pending" && !isMine;
  const canCancel = swap.status === "pending" && isMine;
  const kidNames = (swap.child_ids || [])
    .map((cid) => (childList.find((c) => c.id === cid) || {}).name)
    .filter(Boolean)
    .join(", ");

  return (
    <div
      className={`border rounded-sm p-2.5 sm:p-3 ${compact ? "bg-[#F7FAFA]" : "bg-white"} border-[#D9E1E3]`}
      data-testid={`cs-swap-${swap.id}`}
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border rounded-sm ${badge.cls}`}>
              {badge.label}
            </span>
            <span className="text-sm font-medium text-[#0B2E32]">
              {fmtDate(swap.swap_date)}
            </span>
          </div>
          <div className="text-xs text-[#3F535A] mt-1 leading-snug">
            {nameFor(swap.requested_by, members, placeholder)} asked{" "}
            {nameFor(swap.new_parent_id, members, placeholder)} to take custody
            {kidNames ? <> for <strong>{kidNames}</strong></> : null}.
          </div>
          {swap.reason ? (
            <div className="text-xs text-[#6F828A] mt-0.5 italic">“{swap.reason}”</div>
          ) : null}
          {swap.status !== "pending" && swap.response_note ? (
            <div className="text-[11px] text-[#6F828A] mt-1">
              Note from {nameFor(swap.responded_by, members, placeholder)}: {swap.response_note}
            </div>
          ) : null}
        </div>
        {!compact && (
          <div className="flex items-center gap-1.5 flex-shrink-0" data-testid={`cs-actions-${swap.id}`}>
            {canApprove && (
              <>
                <Button
                  onClick={onApprove}
                  size="sm"
                  className="h-8 px-2.5 bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-sm"
                  data-testid={`cs-approve-${swap.id}`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" strokeWidth={1.8} />
                  Approve
                </Button>
                <Button
                  onClick={onDecline}
                  size="sm"
                  variant="outline"
                  className="h-8 px-2.5 rounded-sm"
                  data-testid={`cs-decline-${swap.id}`}
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" strokeWidth={1.8} />
                  Decline
                </Button>
              </>
            )}
            {canCancel && (
              <Button
                onClick={onCancel}
                size="sm"
                variant="outline"
                className="h-8 px-2.5 rounded-sm text-[#7A2E24] border-[#E1B8B3] hover:bg-[#F3D9D6]"
                data-testid={`cs-cancel-${swap.id}`}
              >
                <X className="w-3.5 h-3.5 mr-1" strokeWidth={1.8} />
                Cancel
              </Button>
            )}
            {!canApprove && !canCancel && (
              <Clock className="w-3.5 h-3.5 text-[#6F828A]" strokeWidth={1.5} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function NewSwapDialog({ open, onOpenChange, otherParent, childList, onCreated }) {
  const [swapDate, setSwapDate] = useState("");
  const [reason, setReason] = useState("");
  const [selectedChildIds, setSelectedChildIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSwapDate("");
      setReason("");
      setSelectedChildIds([]);
    }
  }, [open]);

  const toggleChild = (cid) =>
    setSelectedChildIds((prev) => prev.includes(cid) ? prev.filter((x) => x !== cid) : [...prev, cid]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!swapDate) { toast.error("Pick a date."); return; }
    if (!reason.trim()) { toast.error("Add a short reason so the record is clear."); return; }
    if (!otherParent) { toast.error("No co-parent linked yet."); return; }
    setSaving(true);
    try {
      await api.post("/custody/swap-requests", {
        swap_date: swapDate,
        new_parent_id: otherParent.id,
        child_ids: selectedChildIds,
        reason: reason.trim(),
      });
      toast.success("Swap request sent for approval.");
      onCreated();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not create swap request.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="cs-new-swap-dialog">
        <DialogHeader>
          <DialogTitle>Request a custody swap</DialogTitle>
          <DialogDescription>
            {otherParent
              ? <>Asks <strong>{otherParent.name}</strong> to take custody on the date below. They must approve before the calendar is updated.</>
              : "No co-parent linked yet. Add or invite a co-parent before requesting a swap."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Date</Label>
            <Input
              type="date"
              value={swapDate}
              onChange={(e) => setSwapDate(e.target.value)}
              data-testid="cs-new-date-input"
            />
          </div>
          {childList.length > 0 && (
            <div>
              <Label>Children (optional)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {childList.map((c) => {
                  const on = selectedChildIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleChild(c.id)}
                      className={`px-2.5 py-1 border rounded-sm text-xs transition-colors ${
                        on
                          ? "bg-[#E0F2F2] border-[#A7D8DA] text-[#116B6E]"
                          : "bg-white border-[#D9E1E3] text-[#6F828A] hover:bg-[#EEF3F4]"
                      }`}
                      data-testid={`cs-new-child-${c.id}`}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-[#6F828A] mt-1">
                Leave empty for a household-wide swap.
              </div>
            </div>
          )}
          <div>
            <Label>Reason</Label>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Briefly explain why — this shows on the audit trail."
              data-testid="cs-new-reason-input"
              maxLength={500}
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              type="submit"
              disabled={saving || !otherParent}
              className="bg-[#116B6E] hover:bg-[#0F5658] text-white"
              data-testid="cs-new-submit-btn"
            >
              {saving ? "Sending…" : "Send request"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CustodySwapRequestsCard;
